import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import vehicleService from '../services/vehicleService';
import VehicleCard from '../components/vehicles/VehicleCard';
import VehicleForm from '../components/vehicles/VehicleForm';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import PageHeader from '../components/common/PageHeader';
import Loader from '../components/common/Loader';
import EmptyState from '../components/common/EmptyState';
import toast from 'react-hot-toast';

const COMPLIANCE_FIELDS = [
  'tlb_renewal_due_date',
  'ntsa_inspection_due_date',
  'speed_governor_renewal_due_date',
  'insurance_expiry',
];

const dateOnly = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const dueState = (value) => {
  const dueDate = dateOnly(value);
  if (!dueDate) return null;
  const today = dateOnly(new Date());
  const in30Days = new Date(today);
  in30Days.setDate(in30Days.getDate() + 30);
  if (dueDate < today) return 'overdue';
  if (dueDate <= in30Days) return 'due_soon';
  return 'ok';
};

const vehicleHasState = (vehicle, state) =>
  COMPLIANCE_FIELDS.some((field) => dueState(vehicle[field]) === state);

export default function Vehicles() {
  const location = useLocation();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState({ open: false, vehicle: null });
  const [confirm, setConfirm]   = useState({ open: false, vehicle: null, loading: false });
  const [complianceFilter, setComplianceFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      setVehicles(await vehicleService.getAll());
    } catch {
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const targetReg = location.state?.editVehicleReg;
    if (!targetReg || vehicles.length === 0) return;

    const targetVehicle = vehicles.find((vehicle) => vehicle.registration_number === targetReg);
    if (targetVehicle) {
      setComplianceFilter('all');
      setModal({ open: true, vehicle: targetVehicle });
    }

    navigate(location.pathname, { replace: true, state: null });
  }, [vehicles, location.state, location.pathname, navigate]);

  const onDelete = async () => {
    setConfirm(p => ({ ...p, loading: true }));
    try {
      await vehicleService.delete(confirm.vehicle.id);
      toast.success('Vehicle deleted');
      setConfirm({ open: false, vehicle: null, loading: false });
      load();
    } catch { setConfirm(p => ({ ...p, loading: false })); }
  };

  const filteredVehicles = vehicles.filter((vehicle) => {
    if (complianceFilter === 'all') return true;
    if (complianceFilter === 'overdue') return vehicleHasState(vehicle, 'overdue');
    if (complianceFilter === 'due_soon') return vehicleHasState(vehicle, 'due_soon');
    if (complianceFilter === 'compliant') {
      return !vehicleHasState(vehicle, 'overdue') && !vehicleHasState(vehicle, 'due_soon');
    }
    return true;
  });

  return (
    <div>
      <PageHeader title="Vehicles" subtitle={`${filteredVehicles.length} of ${vehicles.length} vehicles shown`}
        actions={<button className="btn btn-primary" onClick={() => setModal({ open: true, vehicle: null })}>+ Register Vehicle</button>}
      />

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <button className={`btn btn-sm ${complianceFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setComplianceFilter('all')}>All</button>
        <button className={`btn btn-sm ${complianceFilter === 'overdue' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setComplianceFilter('overdue')}>Overdue</button>
        <button className={`btn btn-sm ${complianceFilter === 'due_soon' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setComplianceFilter('due_soon')}>Due Soon (30 days)</button>
        <button className={`btn btn-sm ${complianceFilter === 'compliant' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setComplianceFilter('compliant')}>Compliant</button>
      </div>

      {loading ? <Loader /> : vehicles.length === 0 ? (
        <EmptyState message="No vehicles registered yet" icon="🚐"
          action={<button className="btn btn-primary" onClick={() => setModal({ open: true, vehicle: null })}>Register your first vehicle</button>}
        />
      ) : filteredVehicles.length === 0 ? (
        <EmptyState message="No vehicles match this compliance filter" icon="📅" />
      ) : (
        <div className="grid-3">
          {filteredVehicles.map(v => (
            <VehicleCard key={v.id} vehicle={v}
              onEdit={(v) => setModal({ open: true, vehicle: v })}
              onDelete={(v) => setConfirm({ open: true, vehicle: v, loading: false })}
            />
          ))}
        </div>
      )}

      <Modal isOpen={modal.open} onClose={() => setModal({ open: false, vehicle: null })}
        title={modal.vehicle ? 'Edit Vehicle' : 'Register New Vehicle'} size="lg">
        <VehicleForm vehicle={modal.vehicle} onCancel={() => setModal({ open: false, vehicle: null })}
          onSuccess={() => { setModal({ open: false, vehicle: null }); load(); }} />
      </Modal>

      <ConfirmDialog isOpen={confirm.open} onClose={() => setConfirm({ open: false, vehicle: null, loading: false })}
        onConfirm={onDelete} loading={confirm.loading}
        title="Delete Vehicle" message={`Are you sure you want to delete ${confirm.vehicle?.registration_number}? This will also delete all related trips and expenses.`}
      />
    </div>
  );
}